import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  // LineChart and Line are only used by the "Revenue and orders, month over
  // month" chart, currently commented out below (moving to the future
  // Custom Engagement Overview page) -- re-enable these imports alongside
  // it. Legend is used by the Top Performing Broadcasts chart below.
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { RCS_CAMPAIGNS_ENDPOINT, GET_TENANT_DETAILS } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import typography from "../../cssUtils/typography";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";

const FUNNEL_COLORS = ["#DCE9FB", "#A9CCF5", "#5B9BEA", "#1C64F2", "#0F3D91"];
const FUNNEL_BAR_HEIGHT = 180;
// Sentinel dropdown value representing the aggregate-of-all-campaigns view,
// distinct from any real numeric campaign id.
const OVERVIEW_OPTION = "overview";

const KPITile = ({ label, value }) => (
  <Paper
    sx={{
      p: 2,
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      boxShadow: "none",
      flex: 1,
    }}
  >
    <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography sx={{ ...typography.h2, mt: 0.5 }}>{value}</Typography>
  </Paper>
);

// Columns are equal width; height is proportional to the stage's actual
// value (relative to the largest stage), so the shape reflects real
// drop-off instead of a fixed decorative slope -- a campaign with all-zero
// counts now correctly renders as flat bars instead of a fake funnel shape.
const heightPercentForValue = (value, maxValue) =>
  maxValue > 0 ? Math.max((value / maxValue) * 100, value > 0 ? 4 : 0) : 0;

const FunnelBars = ({ stages }) => {
  const maxValue = Math.max(...stages.map((s) => s.value), 0);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", height: FUNNEL_BAR_HEIGHT }}>
      {stages.map((stage, index) => (
        <Box
          key={stage.name}
          sx={{
            flex: "1 1 0%",
            height: `${heightPercentForValue(stage.value, maxValue)}%`,
            bgcolor: FUNNEL_COLORS[index] || FUNNEL_COLORS[FUNNEL_COLORS.length - 1],
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            pt: 1.5,
            overflow: "hidden",
          }}
        >
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#1B2232", whiteSpace: "nowrap" }}>
            {stage.value.toLocaleString()}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const FunnelLabels = ({ stages }) => (
  <Box sx={{ display: "flex" }}>
    {stages.map((stage) => (
      <Box key={stage.name} sx={{ flex: "1 1 0%", pt: 1, overflow: "hidden" }}>
        <Typography sx={{ ...typography.bodyLarge, color: "text.secondary", whiteSpace: "nowrap" }}>
          {stage.name} {stage.value.toLocaleString()}
        </Typography>
      </Box>
    ))}
  </Box>
);

// Same mapping used in BillingDashboard/UsageTab.js -- kept local since that
// helper isn't exported from a shared module.
const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode?.toUpperCase()) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    case "AED":
      return "د.إ";
    default:
      return "₹"; // Default to Indian Rupee symbol
  }
};

const formatDateOrDash = (value) => (value ? dayjs(value).format("MMM D, YYYY") : "-");

// Default date filter range on mount: start of the current month through
// today, rather than no bound at all -- an unbounded Overview aggregates
// every campaign the tenant has ever run, which is what caused the
// production memory spike (see rcs_campaign/ANALYTICS_NOTES.md). Matches
// the YYYY-MM-DD format both the <input type="date"> value and the
// execution_from_date/execution_to_date query params expect.
const getMonthStartDateString = () => dayjs().startOf("month").format("YYYY-MM-DD");
const getTodayDateString = () => dayjs().format("YYYY-MM-DD");

// Picks the campaign with the latest execution date (logged_at) out of a
// fetched batch, ignoring any that haven't executed yet (logged_at null).
// Used to default the selector to the last-executed campaign on initial
// mount, instead of Overview.
const getMostRecentlyExecutedCampaign = (campaignList) =>
  campaignList.reduce((latest, candidate) => {
    if (!candidate.logged_at) return latest;
    if (!latest || !latest.logged_at) return candidate;
    return new Date(candidate.logged_at) > new Date(latest.logged_at) ? candidate : latest;
  }, null);

const buildCustomerTableColumns = (currencySymbol) => [
  {
    id: "customer_name",
    label: "Customer name",
    render: (row) => row.customer_name || "N/A",
  },
  { id: "phone", label: "Phone number" },
  {
    id: "message_read_or_delivered_at",
    label: "Message Read/Delivered",
    render: (row) => formatDateOrDash(row.message_read_or_delivered_at),
  },
  { id: "orders", label: "Total orders within 30 days" },
  {
    id: "last_order_placed",
    label: "Last order placed",
    render: (row) => formatDateOrDash(row.last_order_placed),
  },
  {
    id: "value",
    label: "Total Value",
    align: "right",
    render: (row) => `${currencySymbol}${row.value}`,
  },
];

const formatPercent = (fraction) => `${Math.round(fraction * 1000) / 10}%`;

const buildBroadcastTableColumns = (currencySymbol) => [
  { id: "campaign_name", label: "Broadcast" },
  { id: "delivered", label: "Delivered" },
  { id: "orders_generated", label: "Orders" },
  {
    id: "order_conversion_rate",
    label: "Order Conversion Rate",
    render: (row) => formatPercent(row.order_conversion_rate),
  },
  { id: "unique_clicks", label: "Unique Clicks" },
  {
    id: "ctr",
    label: "CTR",
    render: (row) => formatPercent(row.ctr),
  },
  {
    id: "revenue_generated",
    label: "Revenue Generated",
    align: "right",
    render: (row) => `${currencySymbol}${row.revenue_generated}`,
  },
];

function RCSCampaignAnalyticsTab() {
  const { getValidToken } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  // Placeholder until the first campaign fetch resolves and picks the
  // last-executed campaign (within the current date filter) as the real
  // default -- see fetchCampaigns. Overview stays available in the
  // dropdown, it's just no longer what loads by default.
  const [selectedCampaignId, setSelectedCampaignId] = useState(OVERVIEW_OPTION);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [error, setError] = useState(null);
  // Tracks whether the one-time "default to last-executed campaign" pick
  // has run yet, so later date-filter-driven refetches fall back to the
  // existing "reset to Overview if selection falls out of range" behavior
  // instead of re-picking a new default every time the filter changes.
  const hasAppliedInitialSelection = useRef(false);

  // Date filter: always visible, whether Overview or a specific campaign is
  // selected. Defaults to month-start -> today (not unbounded -- an
  // unbounded default aggregates every campaign the tenant has ever run,
  // see rcs_campaign/ANALYTICS_NOTES.md). Clearing either field means "no
  // bound" on that side. Filters campaigns by their execution date
  // (logged_at), not created_at.
  const [fromDate, setFromDate] = useState(getMonthStartDateString());
  const [toDate, setToDate] = useState(getTodayDateString());

  // Date filter: same "Log Date" dropdown pattern as the Logs page
  // (RCSBroadcastLogsTable.js) -- an AppDropdown button opening an
  // anchored Menu with From/To date pickers and Cancel/Apply, instead of a
  // centered modal. appliedDateRange holds the in-progress edit; Apply
  // commits it to fromDate/toDate (the values actually used to fetch),
  // Cancel reverts appliedDateRange back to the last-committed values.
  const [appliedDateRange, setAppliedDateRange] = useState([
    fromDate ? dayjs(fromDate) : null,
    toDate ? dayjs(toDate) : null,
  ]);
  const [dateError, setDateError] = useState(null);

  const handleDateRangeApply = () => {
    setFromDate(appliedDateRange[0] ? appliedDateRange[0].format("YYYY-MM-DD") : "");
    setToDate(appliedDateRange[1] ? appliedDateRange[1].format("YYYY-MM-DD") : "");
  };

  const handleDateRangeCancel = () => {
    setAppliedDateRange([
      fromDate ? dayjs(fromDate) : null,
      toDate ? dayjs(toDate) : null,
    ]);
    setDateError(null);
  };

  // "Revenue and orders, month over month" is being moved to a separate
  // Custom Engagement Overview page (tracked in a separate ticket) -- state,
  // effect, and JSX all commented out below rather than removed, so it can
  // be lifted as-is into that page.
  // const [monthlyPerformance, setMonthlyPerformance] = useState([]);
  // const [loadingMonthly, setLoadingMonthly] = useState(false);

  const [billingCurrency, setBillingCurrency] = useState("INR"); // Default to INR

  const [topBroadcasts, setTopBroadcasts] = useState([]);
  const [loadingTopBroadcasts, setLoadingTopBroadcasts] = useState(false);
  const [viewAllBroadcastsOpen, setViewAllBroadcastsOpen] = useState(false);
  const [exportingCustomers, setExportingCustomers] = useState(false);

  const [readToOrderTiming, setReadToOrderTiming] = useState([]);
  const [loadingReadToOrder, setLoadingReadToOrder] = useState(false);

  // Refetches whenever the date filter changes, since the dropdown's
  // campaign list is itself scoped to campaigns executed within that range.
  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoadingCampaigns(true);
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const params = new URLSearchParams({ limit: "20" });
        if (fromDate) params.set("execution_from_date", fromDate);
        if (toDate) params.set("execution_to_date", toDate);
        const response = await fetch(`${RCS_CAMPAIGNS_ENDPOINT}?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-tenant-ID": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch campaigns");
        const data = await response.json();
        const results = data.results || [];
        setCampaigns(results);

        if (!hasAppliedInitialSelection.current) {
          // One-time default: the last-executed campaign within the
          // current date filter, rather than Overview. Only runs once --
          // if there's nothing to pick (empty range), Overview stays the
          // default, and subsequent date-filter changes use the "reset to
          // Overview if selection falls out of range" behavior below
          // instead of re-picking a new default each time.
          hasAppliedInitialSelection.current = true;
          const mostRecent = getMostRecentlyExecutedCampaign(results);
          if (mostRecent) {
            setSelectedCampaignId(mostRecent.id);
          }
        } else if (
          // If the currently selected campaign no longer falls within the
          // date filter, fall back to Overview rather than show stale data
          // for a campaign that's no longer in the list.
          selectedCampaignId !== OVERVIEW_OPTION &&
          !results.some((c) => String(c.id) === String(selectedCampaignId))
        ) {
          setSelectedCampaignId(OVERVIEW_OPTION);
        }
      } catch (err) {
        console.error("Error fetching RCS campaigns:", err.message);
        setError("Failed to load campaigns");
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // Builds the URL for an analytics-family endpoint depending on whether
  // Overview or a specific campaign is selected. Overview always carries
  // the current date filter; a specific campaign's own endpoints don't
  // (campaign_id already pins down exactly one campaign, independent of
  // whatever date range happens to be set).
  const buildScopedUrl = (singleCampaignPath, overviewPath) => {
    if (selectedCampaignId === OVERVIEW_OPTION) {
      const params = new URLSearchParams();
      if (fromDate) params.set("execution_from_date", fromDate);
      if (toDate) params.set("execution_to_date", toDate);
      const qs = params.toString();
      return `${RCS_CAMPAIGNS_ENDPOINT}${overviewPath}${qs ? `?${qs}` : ""}`;
    }
    return `${RCS_CAMPAIGNS_ENDPOINT}${singleCampaignPath}`;
  };

  // Downloads every customer meeting the table's filter criteria (not just
  // the top 10 shown on screen) as a CSV. Fetched with the auth header
  // (a plain <a href> can't carry Authorization) then saved via a
  // synthetic link, since this is an authenticated endpoint.
  const handleExportTopCustomers = async () => {
    setExportingCustomers(true);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const url = buildScopedUrl(
        `${selectedCampaignId}/top-customers-export/`,
        "overview-top-customers-export/",
      );
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-tenant-ID": tenant,
        },
      });
      if (!response.ok) throw new Error("Failed to export top customers");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "top_customers.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error exporting top customers:", err.message);
    } finally {
      setExportingCustomers(false);
    }
  };

  useEffect(() => {
    if (!selectedCampaignId) return;

    const fetchAnalytics = async () => {
      setLoadingAnalytics(true);
      setError(null);
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const url = buildScopedUrl(`${selectedCampaignId}/analytics/`, "overview-analytics/");
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-tenant-ID": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch campaign analytics");
        const data = await response.json();
        setAnalytics(data);
      } catch (err) {
        console.error("Error fetching campaign analytics:", err.message);
        setError("Failed to load campaign analytics");
        setAnalytics(null);
      } finally {
        setLoadingAnalytics(false);
      }
    };

    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, fromDate, toDate]);

  // Moved to the future Custom Engagement Overview page -- see the state
  // declarations above for why this is commented rather than removed.
  // useEffect(() => {
  //   if (!selectedCampaignId) return;
  //
  //   const fetchMonthlyPerformance = async () => {
  //     setLoadingMonthly(true);
  //     try {
  //       const token = await getValidToken();
  //       const tenant = localStorage.getItem("tenant");
  //       const response = await fetch(
  //         `${RCS_CAMPAIGNS_ENDPOINT}monthly-performance/?campaign_id=${selectedCampaignId}`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //             "X-tenant-ID": tenant,
  //           },
  //         },
  //       );
  //       if (!response.ok) throw new Error("Failed to fetch monthly performance");
  //       const data = await response.json();
  //       setMonthlyPerformance(data.monthly_performance || []);
  //     } catch (err) {
  //       console.error("Error fetching monthly performance:", err.message);
  //       setMonthlyPerformance([]);
  //     } finally {
  //       setLoadingMonthly(false);
  //     }
  //   };
  //
  //   fetchMonthlyPerformance();
  // }, [selectedCampaignId, getValidToken]);

  // Fetch tenant details to get billing currency
  useEffect(() => {
    const fetchTenantDetails = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");

        const response = await fetch(GET_TENANT_DETAILS, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-ID": tenant,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (
          data.results &&
          data.results.length > 0 &&
          data.results[0].billing_currency
        ) {
          setBillingCurrency(data.results[0].billing_currency);
        }
      } catch (error) {
        console.error("Error fetching tenant details:", error);
      }
    };

    fetchTenantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scoped to the selected campaign's own execution-date -> +30-day window,
  // or (Overview) every campaign in the date filter, ranked by revenue.
  useEffect(() => {
    if (!selectedCampaignId) return;

    const fetchTopBroadcasts = async () => {
      setLoadingTopBroadcasts(true);
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const url = buildScopedUrl(
          `top-performing-broadcasts/?campaign_id=${selectedCampaignId}`,
          "overview-top-performing-broadcasts/",
        );
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-tenant-ID": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch top performing broadcasts");
        const data = await response.json();
        setTopBroadcasts(data.broadcasts || []);
      } catch (err) {
        console.error("Error fetching top performing broadcasts:", err.message);
        setTopBroadcasts([]);
      } finally {
        setLoadingTopBroadcasts(false);
      }
    };

    fetchTopBroadcasts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, fromDate, toDate]);

  // Scoped to the selected campaign's own execution-date -> +30-day window,
  // or (Overview) summed across every campaign in the date filter.
  useEffect(() => {
    if (!selectedCampaignId) return;

    const fetchReadToOrderTiming = async () => {
      setLoadingReadToOrder(true);
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const url = buildScopedUrl(
          `read-to-order-timing/?campaign_id=${selectedCampaignId}`,
          "overview-read-to-order-timing/",
        );
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-tenant-ID": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch read-to-order timing");
        const data = await response.json();
        setReadToOrderTiming(data.read_to_order_timing || []);
      } catch (err) {
        console.error("Error fetching read-to-order timing:", err.message);
        setReadToOrderTiming([]);
      } finally {
        setLoadingReadToOrder(false);
      }
    };

    fetchReadToOrderTiming();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, fromDate, toDate]);

  const currencySymbol = getCurrencySymbol(billingCurrency);
  const customerTableColumns = buildCustomerTableColumns(currencySymbol);
  const broadcastTableColumns = buildBroadcastTableColumns(currencySymbol);

  // Top 5 for the chart; "View all" shows the full topBroadcasts list
  // (already sorted by revenue, highest first, by the backend).
  const topBroadcastsChartData = topBroadcasts.slice(0, 5).map((b) => ({
    name: b.campaign_name,
    orderConversionRatePct: Math.round(b.order_conversion_rate * 1000) / 10,
    ctrPct: Math.round(b.ctr * 1000) / 10,
    revenue: b.revenue_generated,
  }));

  const funnelStages = analytics
    ? [
        { name: "Sent", value: analytics.messages_sent },
        { name: "Delivered", value: analytics.delivered },
        { name: "Read", value: analytics.read },
        { name: "Ordered", value: analytics.orders_generated },
      ]
    : [];

  return (
    <Container maxWidth={false} sx={{ my: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography sx={{ ...typography.h1 }}>RCS Campaign Analytics</Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AppDropdown
            sx={{
              whiteSpace: "nowrap",
              "& .MuiChip-root": {
                maxWidth: fromDate && toDate ? "120px" : "0px",
                transition: "max-width 0.2s ease",
              },
            }}
            paperProps={{ maxWidth: "500px" }}
            menuContent={(closeMenu) => (
              <Box sx={{ p: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="subtitle2">Select Log Date:</Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <DatePicker
                        label="From"
                        value={appliedDateRange[0]}
                        onChange={(newValue) => {
                          setAppliedDateRange([newValue, appliedDateRange[1]]);
                          if (
                            appliedDateRange[1] &&
                            newValue &&
                            dayjs(newValue).isAfter(appliedDateRange[1])
                          )
                            setDateError("From Date must be less than or equal to To Date");
                          else setDateError(null);
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { width: "200px", "& .MuiInputBase-root": { height: "40px" } },
                          },
                        }}
                      />
                      <DatePicker
                        label="To"
                        value={appliedDateRange[1]}
                        onChange={(newValue) => {
                          setAppliedDateRange([appliedDateRange[0], newValue]);
                          if (
                            appliedDateRange[0] &&
                            newValue &&
                            dayjs(newValue).isBefore(appliedDateRange[0])
                          )
                            setDateError("To Date must be greater than or equal to From Date");
                          else setDateError(null);
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { width: "200px", "& .MuiInputBase-root": { height: "40px" } },
                          },
                        }}
                      />
                    </Box>
                    {dateError && (
                      <Typography variant="caption" color="error">
                        {dateError}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button
                        onClick={() => {
                          handleDateRangeCancel();
                          closeMenu();
                        }}
                        variant="outlined"
                      >
                        CANCEL
                      </Button>
                      <Button
                        onClick={() => {
                          handleDateRangeApply();
                          closeMenu();
                        }}
                        variant="contained"
                        disabled={!appliedDateRange[0] || !appliedDateRange[1] || Boolean(dateError)}
                      >
                        APPLY
                      </Button>
                    </Box>
                  </Box>
                </LocalizationProvider>
              </Box>
            )}
          >
            Log Date
            {fromDate && toDate && (
              <Tooltip
                title={`${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}`}
                placement="top"
                arrow
              >
                <Chip label={`${fromDate} - ${toDate}`} size="small" sx={{ ml: 1, overflow: "hidden" }} />
              </Tooltip>
            )}
          </AppDropdown>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
              disabled={loadingCampaigns}
            >
              <MenuItem value={OVERVIEW_OPTION}>Overview</MenuItem>
              {campaigns.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name || `Campaign ${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {analytics && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
          <Box>
            <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
              Campaign name
            </Typography>
            <Typography sx={{ ...typography.h2 }}>{analytics.campaign_name || "-"}</Typography>
          </Box>
          <Box>
            <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
              Description
            </Typography>
            <Typography sx={{ ...typography.h2 }}>
              {analytics.campaign_description || "-"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
              Execution date
            </Typography>
            <Typography sx={{ ...typography.h2 }}>
              {analytics.campaign_execution_date
                ? dayjs(analytics.campaign_execution_date).format("MMM D, YYYY")
                : "-"}
            </Typography>
          </Box>
        </Box>
      )}

      {loadingAnalytics ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : analytics ? (
        <>
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <KPITile label="Messages Sent" value={analytics.messages_sent} />
            <KPITile
              label="Campaign Cost"
              value={`${currencySymbol}${analytics.campaign_cost}`}
            />
            <KPITile label="Orders Generated" value={analytics.orders_generated} />
            <KPITile
              label="Revenue Driven"
              value={`${currencySymbol}${analytics.revenue_driven}`}
            />
            <KPITile
              label="AOV"
              value={`${currencySymbol}${analytics.average_order_value}`}
            />
          </Box>

          <Paper
            sx={{
              p: 3,
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              boxShadow: "none",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ ...typography.h3 }}>Funnel</Typography>
              <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
                ROAS {analytics.roas}x &middot; cost per order {currencySymbol}
                {analytics.cost_per_order}
              </Typography>
            </Box>
            <FunnelBars stages={funnelStages} />
            <FunnelLabels stages={funnelStages} />
          </Paper>

          <Paper
            sx={{
              p: 3,
              mt: 3,
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              boxShadow: "none",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ ...typography.h3 }}>Top customers by order value</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleExportTopCustomers}
                disabled={exportingCustomers || (analytics.top_customers || []).length === 0}
              >
                {exportingCustomers ? "Exporting..." : "Export"}
              </Button>
            </Box>
            <AppTable
              columns={customerTableColumns}
              rows={analytics.top_customers || []}
              rowKey={(row) => row.phone}
              emptyMessage="No matched orders for this campaign yet"
              stickyHeader={false}
              autoAdjustHeight={false}
              maxHeight="none"
            />
          </Paper>
        </>
      ) : (
        <Grid />
      )}

      {/* Scoped to the selected campaign's execution-date -> +30-day window */}
      <Paper
        sx={{
          p: 3,
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          boxShadow: "none",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ ...typography.h3 }}>Top Performing Broadcasts</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setViewAllBroadcastsOpen(true)}
            disabled={topBroadcasts.length === 0}
          >
            View all
          </Button>
        </Box>
        {loadingTopBroadcasts ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 320, width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topBroadcastsChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 12 }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <YAxis
                  yAxisId="rate"
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="revenue"
                  orientation="right"
                  tickFormatter={(v) => `${currencySymbol}${v}`}
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip
                  formatter={(value, name) =>
                    name === "Revenue Generated" ? `${currencySymbol}${value}` : `${value}%`
                  }
                />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: "10px" }} />
                <Bar yAxisId="rate" dataKey="orderConversionRatePct" name="Order Conversion Rate" fill="#1C64F2" radius={[4, 4, 0, 0]} barSize={24} />
                <Bar yAxisId="rate" dataKey="ctrPct" name="CTR" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={24} />
                <Bar yAxisId="revenue" dataKey="revenue" name="Revenue Generated" fill="#22C55E" radius={[4, 4, 0, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      <Dialog open={viewAllBroadcastsOpen} onClose={() => setViewAllBroadcastsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>All broadcasts</DialogTitle>
        <DialogContent>
          <AppTable
            columns={broadcastTableColumns}
            rows={topBroadcasts}
            rowKey={(row) => row.campaign_id}
            emptyMessage="No broadcasts in the selected date range"
            stickyHeader={false}
            autoAdjustHeight={false}
            maxHeight="none"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAllBroadcastsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Scoped to the selected campaign's execution-date -> +30-day window */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 3,
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              boxShadow: "none",
              height: "100%",
            }}
          >
            <Typography sx={{ ...typography.h3, mb: 2 }}>
              Time to Order
            </Typography>
            {loadingReadToOrder ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 280, width: "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={readToOrderTiming} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="bucket" tick={{ fill: "#6B7280", fontSize: 12 }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fill: "#6B7280", fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="percentage" name="% of orders" fill="#1C64F2" radius={[4, 4, 0, 0]} barSize={60} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/*
      Moved to the future Custom Engagement Overview page (separate ticket) --
      commented out rather than removed so it can be lifted as-is.

      <Paper
        sx={{
          p: 3,
          mt: 3,
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          boxShadow: "none",
        }}
      >
        <Typography sx={{ ...typography.h3, mb: 2 }}>
          Revenue and orders, month over month
        </Typography>
        {loadingMonthly ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 320, width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyPerformance} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 12 }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <YAxis
                  yAxisId="revenue"
                  tickFormatter={(v) => `${currencySymbol}${v}`}
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="orders"
                  orientation="right"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: "10px" }} />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#1C64F2"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  yAxisId="orders"
                  type="monotone"
                  dataKey="orders"
                  name="Orders"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
      */}
    </Container>
  );
}

export default RCSCampaignAnalyticsTab;
